import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import PromptInput from "@/components/PromptInput";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { classifyError } from "@/lib/fetchWithTimeout";
import { useBuilderConversation } from "@/hooks/useBuilderConversation";
import { useBuilderAttachments } from "@/hooks/useBuilderAttachments";
import { useLibraryRecent, type LibraryPrompt } from "@/hooks/useLibraryData";
import { useCommandStats, WEEKLY_GOAL } from "@/hooks/useCommandStats";
import { useWorkflowRail, type WorkflowRailItem } from "@/hooks/useWorkflowRail";
import { getStoredMode, setStoredMode, type PromptInputMode } from "@/lib/promptBuilderUtils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Flame,
  GitBranch,
  Hammer,
  Repeat,
  Wand2,
} from "lucide-react";
import type { RemixContext } from "@/types/promptBuilder";

interface ActivityItem {
  icon: typeof CheckCircle2;
  label: string;
  onClick: () => void;
}

interface HistoricalResultState {
  ui?: {
    score_id?: string;
  };
}

function formatRankPercentile(rank: number | null, memberCount: number | null): string {
  if (!rank || !memberCount) return "--";
  const percentile = Math.ceil((rank / memberCount) * 100);
  return `Top ${Math.min(100, Math.max(1, percentile))}%`;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function draftProgress(turnCount: number) {
  return Math.min(100, Math.max(25, (Math.min(turnCount || 1, 4) / 4) * 100));
}

function getPromptTitle(prompt: LibraryPrompt) {
  const text = prompt.best_prompt || prompt.original_content || prompt.detected_type || "Untitled prompt";
  return text.length > 72 ? `${text.slice(0, 72)}...` : text;
}

function getWorkflowTitle(workflow: WorkflowRailItem) {
  const text = workflow.workflow_json?.name || workflow.workflow_json?.objective || workflow.initial_goal || "Untitled workflow";
  return text.length > 64 ? `${text.slice(0, 64)}...` : text;
}

function getWorkflowTime(workflow: WorkflowRailItem) {
  return workflow.completed_at || workflow.started_at;
}

function workflowNeedsUpdate(workflow: WorkflowRailItem) {
  const updatedAt = new Date(getWorkflowTime(workflow)).getTime();
  return Number.isFinite(updatedAt) && Date.now() - updatedAt > 7 * 24 * 60 * 60 * 1000;
}

const PromptPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, membership, profileName, onboardingCompleted, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [commandMode, setCommandMode] = useState<PromptInputMode>("build");

  // Data hooks
  const { stats: commandStats, loading: statsLoading } = useCommandStats(user?.id, membership?.org_id);
  const { liveWorkflows, inProgressWorkflows, totalCount: totalWorkflowCount, loading: workflowsLoading } = useWorkflowRail(user?.id);

  // File attachment support for build mode (lazy conversation creation)
  const { startConversation, conversationId } = useBuilderConversation();
  const { attachments, uploadFile, removeAttachment, acceptString } = useBuilderAttachments();
  const { data: recentPrompts = [], isLoading: recentLoading } = useLibraryRecent(user?.id);
  const conversationIdRef = useRef<string | null>(null);
  // Keep ref in sync so async file handlers always see the latest ID
  if (conversationId) conversationIdRef.current = conversationId;

  const isUploading = attachments.some(a => a.status === 'uploading' || a.status === 'extracting');
  const firstName = useMemo(() => {
    const name = profileName || user?.user_metadata?.full_name || user?.email || "";
    return name.split(" ")[0].split("@")[0] || undefined;
  }, [profileName, user?.email, user?.user_metadata?.full_name]);
  const needUpdateWorkflows = useMemo(() => liveWorkflows.filter(workflowNeedsUpdate), [liveWorkflows]);
  const workflowList = useMemo(() => [
    ...inProgressWorkflows,
    ...liveWorkflows,
  ].slice(0, 5), [inProgressWorkflows, liveWorkflows]);

  useEffect(() => {
    const stored = getStoredMode();
    setCommandMode(stored);
  }, []);

  const handleFileSelect = useCallback(async (files: FileList) => {
    try {
      // Lazily create a conversation on first file upload
      let cid = conversationIdRef.current;
      if (!cid) {
        cid = await startConversation();
        if (!cid) {
          toast.error('Please sign in to attach files');
          return;
        }
        conversationIdRef.current = cid;
      }

      for (const file of Array.from(files)) {
        try {
          await uploadFile(file, cid);
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Upload failed');
        }
      }
    } catch (err) {
      toast.error('Failed to prepare file upload');
      console.error('[PromptPage] File upload error:', err);
    }
  }, [startConversation, uploadFile]);

  const handleModeChange = useCallback((nextMode: PromptInputMode) => {
    setCommandMode(nextMode);
    setStoredMode(nextMode);
  }, []);

  const focusComposer = useCallback((nextMode: PromptInputMode) => {
    handleModeChange(nextMode);
    requestAnimationFrame(() => {
      document.getElementById("prompt-command-composer")?.focus();
    });
  }, [handleModeChange]);

  const openPrompt = useCallback((prompt: LibraryPrompt) => {
    navigate(`/prompt_score/${prompt.score_id}`);
  }, [navigate]);

  const openWorkflow = useCallback((workflow: WorkflowRailItem) => {
    if (workflow.status === "in_progress") {
      navigate('/workflows', {
        state: {
          initialGoal: workflow.initial_goal || workflow.workflow_json?.objective || workflow.workflow_json?.name || "",
          openBuilder: true,
        },
      });
      return;
    }

    navigate('/workflows');
  }, [navigate]);

  const recentActivities = useMemo<ActivityItem[]>(() => {
    const latestPrompt = recentPrompts[0];
    const latestWorkflow = inProgressWorkflows[0];

    return [
      {
        icon: CheckCircle2,
        label: "Did your last prompt work?",
        onClick: () => latestPrompt ? openPrompt(latestPrompt) : navigate("/library"),
      },
      {
        icon: Flame,
        label: "Keep your streak",
        onClick: () => focusComposer("build"),
      },
      {
        icon: Repeat,
        label: "Check your workflows",
        onClick: () => latestWorkflow ? openWorkflow(latestWorkflow) : navigate("/workflows"),
      },
    ];
  }, [focusComposer, inProgressWorkflows, navigate, openPrompt, openWorkflow, recentPrompts]);
  // Redirect authenticated users who need to complete onboarding
  useEffect(() => {
    if (!authLoading && user && onboardingCompleted === false) {
      navigate('/onboarding', { replace: true });
    }
  }, [user, onboardingCompleted, authLoading, navigate]);

  // Handle historical result navigation and remix context from router state
  useEffect(() => {
    const state = location.state as { historicalResult?: HistoricalResultState; analyzePrompt?: string; remixContext?: RemixContext } | null;
    if (state?.historicalResult) {
      const hScoreId = state.historicalResult?.ui?.score_id || 'view';
      navigate(`/prompt_score/${hScoreId}`, { state: { result: state.historicalResult }, replace: true });
    }
    if (state?.remixContext) {
      navigate('/build/new', { state: { remixContext: state.remixContext, returnTo: '/prompt' }, replace: true });
    }
    // Clear state
    if (state && !state.remixContext) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state, navigate]);

  const handleAnalyze = async (prompt: string, conversationId?: string | null) => {
    setIsLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 300_000);
    try {
      // Fire scoring and tool recommendations in parallel
      const [scoreResult, toolRecResult] = await Promise.allSettled([
        supabase.functions.invoke('score-submission', {
          body: {
            content: prompt,
            submission_type: 'prompt_single',
            source: 'app',
            conversation_id: conversationId || undefined,
          }
        }),
        supabase.functions.invoke('recommend-tools-inline-v6', {
          body: { final_prompt: prompt }
        }),
      ]);
      clearTimeout(timer);

      // Process score result (required)
      if (scoreResult.status === 'rejected') {
        throw scoreResult.reason;
      }
      const { data, error } = scoreResult.value;
      if (error) {
        console.error('Error analyzing prompt:', error);
        const classified = classifyError(error);
        toast.error(classified.message);
        throw error;
      }
      if (data.error) {
        toast.error(data.error);
        throw new Error(data.error);
      }

      // Show sensitivity toast if flagged
      if (data.sensitivity_flag) {
        toast.info("This prompt contains sensitive information and has been saved privately in your library.");
      }

      // Extract tool recommendation data (optional — graceful degradation)
      let toolRecommendations = null;
      let recommendationId = null;
      if (toolRecResult.status === 'fulfilled') {
        const toolData = toolRecResult.value.data;
        if (toolData && !toolData.error && toolData.recommendations) {
          toolRecommendations = toolData.recommendations;
          recommendationId = toolData.recommendation_id;

          // Link recommendation to submission
          if (recommendationId && data.submission_id) {
            supabase
              .from('tool_recommendations')
              .update({ submission_id: data.submission_id })
              .eq('id', recommendationId)
              .then(({ error: linkErr }) => {
                if (linkErr) console.error('Failed to link tool rec to submission:', linkErr);
              });
          }
        }
      }

      const transformedResult = {
        ui: {
          ...data.analysis,
          score_id: data.score_id,
          submission_id: data.submission_id,
          is_public: !data.sensitivity_flag,
          original_score: data.original_score,
          improved_score: data.overall_score,
          user_id: user?.id,
        },
        telemetry: {
          tags: data.analysis?.tags || []
        }
      };

      navigate(`/prompt_score/${data.score_id}`, {
        state: {
          result: transformedResult,
          toolRecommendations,
          recommendationId,
          originalPrompt: prompt,
        }
      });
    } catch (err) {
      clearTimeout(timer);
      console.error('Error:', err);
      const classified = classifyError(err);
      toast.error(classified.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuildPrompt = (initialGoal: string) => {
    navigate('/build/new', {
      state: {
        initialGoal,
        returnTo: '/prompt',
        attachmentConversationId: conversationIdRef.current || undefined,
      },
    });
  };

  const handleBuildWorkflow = (initialGoal: string) => {
    navigate('/workflows', {
      state: {
        initialGoal,
        openBuilder: true,
      },
    });
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background lg:h-screen lg:min-h-0">
        <div className="grid min-h-screen lg:h-screen lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="flex min-h-[calc(100vh-3.5rem)] flex-col px-4 py-6 md:px-8 lg:h-screen lg:min-h-0 lg:py-8">
            <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center">
              <PromptInput
                onAnalyze={handleAnalyze}
                onBuildPrompt={handleBuildPrompt}
                onBuildWorkflow={handleBuildWorkflow}
                isLoading={isLoading}
                attachments={attachments}
                onFileSelect={handleFileSelect}
                onRemoveAttachment={removeAttachment}
                acceptString={acceptString}
                isUploading={isUploading}
                showAttachments={!!user}
                greetingName={firstName}
                mode={commandMode}
                onModeChange={handleModeChange}
              />

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => focusComposer("build")}
                  className="rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/30"
                >
                  <Hammer className="mb-3 h-5 w-5 text-primary" />
                  <p className="font-semibold text-foreground">Build a prompt</p>
                  <p className="mt-1 text-sm text-muted-foreground">Start from an outcome and let Cosmo structure it.</p>
                </button>
                <button
                  type="button"
                  onClick={() => focusComposer("improve")}
                  className="rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/30"
                >
                  <Wand2 className="mb-3 h-5 w-5 text-primary" />
                  <p className="font-semibold text-foreground">Improve a prompt</p>
                  <p className="mt-1 text-sm text-muted-foreground">Paste a draft and get a stronger version.</p>
                </button>
                <button
                  type="button"
                  onClick={() => focusComposer("workflow")}
                  className="rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <GitBranch className="mb-3 h-5 w-5 text-primary" />
                  <p className="font-semibold text-foreground">Workflow</p>
                  <p className="mt-1 text-sm text-muted-foreground">Map a repeatable process with steps and handoffs.</p>
                </button>
              </div>

              <div className="mt-5 grid gap-2 sm:grid-cols-3">
                {recentLoading || workflowsLoading ? (
                  <>
                    <Skeleton className="h-11 rounded-full" />
                    <Skeleton className="h-11 rounded-full" />
                    <Skeleton className="h-11 rounded-full" />
                  </>
                ) : (
                  recentActivities.map((activity) => {
                    const ActivityIcon = activity.icon;

                    return (
                      <Button
                        key={activity.label}
                        variant="outline"
                        className="h-auto min-h-11 justify-start gap-2 rounded-full px-4 py-2 text-left"
                        onClick={activity.onClick}
                      >
                        <ActivityIcon className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 truncate">{activity.label}</span>
                      </Button>
                    );
                  })
                )}
              </div>
            </div>
          </section>

          <aside className="border-t border-border bg-sidebar/30 px-4 py-5 md:px-6 lg:h-screen lg:overflow-y-auto lg:border-l lg:border-t-0 lg:px-4">
            <div className="mx-auto max-w-3xl lg:max-w-none">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Command rail</p>
                <Button variant="ghost" size="sm" onClick={() => navigate("/stats")} className="h-7 gap-1 px-2 text-xs">
                  Stats <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="space-y-4">
                <section className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Keep your prompt streak going</p>
                      {statsLoading ? (
                        <Skeleton className="mt-2 h-8 w-24" />
                      ) : (
                        <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                          {commandStats.promptsThisWeek}/{WEEKLY_GOAL}
                        </p>
                      )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Flame className="h-6 w-6" />
                    </div>
                  </div>
                  <div
                    className="mt-4 grid grid-cols-3 gap-1.5"
                    role="progressbar"
                    aria-label="Weekly prompt streak progress"
                    aria-valuemin={0}
                    aria-valuemax={WEEKLY_GOAL}
                    aria-valuenow={Math.min(commandStats.promptsThisWeek, WEEKLY_GOAL)}
                  >
                    {Array.from({ length: WEEKLY_GOAL }).map((_, index) => (
                      <div
                        key={index}
                        className={`h-2 rounded-full transition-colors ${
                          commandStats.promptsThisWeek > index ? "bg-primary" : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">
                    {commandStats.promptsThisWeek >= WEEKLY_GOAL
                      ? "Weekly streak saved. Keep building while the momentum is warm."
                      : `${WEEKLY_GOAL - commandStats.promptsThisWeek} more prompt${WEEKLY_GOAL - commandStats.promptsThisWeek === 1 ? "" : "s"} to save this week's streak.`}
                  </p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded-md border border-border bg-muted/20 p-2 text-center">
                      <p className="text-lg font-semibold tabular-nums">
                        {formatRankPercentile(commandStats.teamRank, commandStats.teamMemberCount)}
                      </p>
                      <p className="text-xs text-muted-foreground">Rank</p>
                    </div>
                    <div className="rounded-md border border-border bg-muted/20 p-2 text-center">
                      <p className="text-lg font-semibold tabular-nums">{commandStats.totalPrompts}</p>
                      <p className="text-xs text-muted-foreground">Prompts</p>
                    </div>
                    <div className="rounded-md border border-border bg-muted/20 p-2 text-center">
                      <p className="text-lg font-semibold tabular-nums">{totalWorkflowCount}</p>
                      <p className="text-xs text-muted-foreground">Workflows</p>
                    </div>
                  </div>
                </section>

                <section className="rounded-lg border border-border bg-card p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">Workflows</p>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => navigate("/workflows")}>
                      See all
                    </Button>
                  </div>

                  <div className="mb-4 grid grid-cols-3 gap-2">
                    <div className="rounded-md border border-border bg-muted/20 p-2 text-center">
                      <div className="flex items-center justify-center gap-1.5 text-primary">
                        <Repeat className="h-3.5 w-3.5" />
                        <p className="text-lg font-semibold tabular-nums text-foreground">{liveWorkflows.length}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">Live</p>
                    </div>
                    <div className="rounded-md border border-border bg-muted/20 p-2 text-center">
                      <div className="flex items-center justify-center gap-1.5 text-primary">
                        <GitBranch className="h-3.5 w-3.5" />
                        <p className="text-lg font-semibold tabular-nums text-foreground">{inProgressWorkflows.length}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                    </div>
                    <div className="rounded-md border border-border bg-muted/20 p-2 text-center">
                      <div className="flex items-center justify-center gap-1.5 text-primary">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <p className="text-lg font-semibold tabular-nums text-foreground">{needUpdateWorkflows.length}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">Update</p>
                    </div>
                  </div>

                  {workflowsLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-14" />
                      <Skeleton className="h-14" />
                      <Skeleton className="h-14" />
                    </div>
                  ) : workflowList.length > 0 ? (
                    <div className="space-y-2">
                      {workflowList.map((workflow) => {
                        const isInProgress = workflow.status === "in_progress";
                        const needsUpdate = !isInProgress && workflowNeedsUpdate(workflow);
                        const WorkflowIcon = isInProgress ? GitBranch : Repeat;

                        return (
                          <button
                            type="button"
                            key={workflow.id}
                            onClick={() => openWorkflow(workflow)}
                            className="w-full rounded-md border border-border bg-background p-3 text-left transition-colors hover:border-primary/50"
                          >
                            <div className="flex items-start gap-2">
                              <WorkflowIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="truncate text-sm font-medium text-foreground">{getWorkflowTitle(workflow)}</p>
                                  <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] leading-4 text-muted-foreground">
                                    {isInProgress ? "In progress" : needsUpdate ? "Needs update" : "Live"}
                                  </span>
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {isInProgress
                                    ? `${formatRelativeTime(workflow.started_at)} · ${workflow.turn_count || 1} turn${(workflow.turn_count || 1) === 1 ? "" : "s"}`
                                    : `${workflow.cadence === "perpetual" ? "Perpetual" : "Recurring"} · ${workflow.workflow_json?.steps?.length || 0} steps`}
                                </p>
                                {isInProgress && <Progress value={draftProgress(workflow.turn_count)} className="mt-2 h-1" />}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border p-3">
                      <p className="text-sm text-muted-foreground">No workflows yet. Start one when the work has repeatable steps.</p>
                      <Button size="sm" className="mt-3 gap-1.5" onClick={() => focusComposer("workflow")}>
                        Start workflow <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </aside>
        </div>

      </div>
    </MainLayout>
  );
};

export default PromptPage;
